'use client';

import { TranslatableButton, TranslatableLoadingButton } from '@epos/ui/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, TextField, Typography } from '@mui/material';
import Link from 'next/link';
import { useParams, useRouter } from 'next/navigation';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import PhoneCodeField from '@/components/form-fields/phone-code-field';
import { useQueryConfigs } from '@/query/hooks/use-query-configs';

import { sendOtpAction } from '../../_actions/send-otp-action';
import { checkEmailAction } from '../../_actions/check-email-action';
import { ForgotPasswordFormProps } from './forgot-password-form.props';
import {
  ForgotPasswordFormDto,
  ForgotPasswordFormFieldNames,
  forgotPasswordFormSchema
} from './forgot-password-form.schema';

const SUBMIT_BUTTON_LABEL = 'Continue';
const BACK_TO_LOGIN_LABEL = 'Back';
const EMAIL_SENT_TOAST = 'Reset code has been sent to your email address';
const WHATSAPP_SENT_TOAST = 'Reset code has been sent to your phone number';

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = ({ subdomain }) => {
  const {
    control,
    handleSubmit,
    formState: { isSubmitting }
  } = useForm<ForgotPasswordFormDto>({
    resolver: yupResolver(forgotPasswordFormSchema),
    defaultValues: {
      [ForgotPasswordFormFieldNames.phoneCode]: '+65',
      [ForgotPasswordFormFieldNames.phoneNumber]: ''
    }
  });
  const router = useRouter();
  const params = useParams();
  const { data: configs } = useQueryConfigs();

  const successHandler = async (form: ForgotPasswordFormDto) => {
    try {
      const { hasEmail, message: checkMessage } = await checkEmailAction(
        subdomain,
        form.phoneCode,
        form.phoneNumber
      );

      if (checkMessage) {
        toast.error(checkMessage);
        return;
      }

      // No email on file: fall back to WhatsApp OTP when WATI is configured, otherwise block
      const channel = hasEmail ? 'email' : 'whatsapp';

      if (!hasEmail && !configs?.shouldShowTryAnotherMethod) {
        router.push(`/${params.subdomain}/forgot-password/no-email`);
        return;
      }

      const { status, message } = await sendOtpAction(
        subdomain,
        form.phoneCode,
        form.phoneNumber,
        channel
      );
      if (!status) {
        toast.error(message);
        return;
      }
      router.push(`/${params.subdomain}/forgot-password/verify`);
      toast.success(channel === 'email' ? EMAIL_SENT_TOAST : WHATSAPP_SENT_TOAST);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset code. Please try again.';
      toast.error(errorMessage);
    }
  };

  return (
    <form noValidate className='flex w-full flex-col gap-8' onSubmit={handleSubmit(successHandler)}>
      <Box>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
          Phone Number&nbsp;<span style={{ color: '#d32f2f' }}>*</span>
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: '120px', maxWidth: '140px', flex: '0 0 auto' }}>
            <Controller
              control={control}
              name={ForgotPasswordFormFieldNames.phoneCode}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <PhoneCodeField
                  onChange={onChange}
                  value={value}
                  error={Boolean(error)}
                  helperText={error?.message}
                  placeholder='+65'
                />
              )}
            />
          </Box>

          <Box sx={{ flex: '1 1 auto' }}>
            <Controller
              control={control}
              name={ForgotPasswordFormFieldNames.phoneNumber}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <TextField
                  fullWidth
                  type='number'
                  onChange={onChange}
                  value={value}
                  error={Boolean(error)}
                  helperText={error?.message}
                  sx={{
                    '& input[type=number]': {
                      '-moz-appearance': 'textfield'
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                      '-webkit-appearance': 'none',
                      margin: 0
                    }
                  }}
                />
              )}
            />
          </Box>
        </Box>
      </Box>

      <div className='flex w-full flex-col gap-2'>
        <TranslatableLoadingButton type='submit' className='flex-grow' loading={isSubmitting}>
          {SUBMIT_BUTTON_LABEL}
        </TranslatableLoadingButton>
        <TranslatableButton
          variant='outlined'
          className='!border-1.5 !flex-grow'
          component={Link}
          href={`/${params.subdomain}/login`}
          disabled={isSubmitting}
        >
          {BACK_TO_LOGIN_LABEL}
        </TranslatableButton>
      </div>
    </form>
  );
};

export default ForgotPasswordForm;
